/* eslint-disable @typescript-eslint/no-use-before-define */
import { Text } from '@dropbox/dig-components/typography';
import SignatureProviderImpl from 'signer-app/signature-modal/signature-modal-context/signature-provider-impl';
import React, { useCallback } from 'react';
import SignerDocument from 'signer-app/signer-signature-document/document';
import SignerContext, {
  signerContext,
} from 'signer-app/signer-signature-document/context';
import { SignerContextShape } from 'signer-app/signer-signature-document/types';
import TouchProvider from 'signer-app/parts/touch-provider';
import {
  useTouchField,
  useValidation,
} from 'signer-app/signer-experience/use-validation';
import { ValidationComponent } from 'signer-app/signer-experience/validation-component';
import styles from 'signer-app/signer-experience/signer-experience.module.css';
import classnames from 'classnames';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import invariant from 'invariant';
import { SignerToolbar } from 'signer-app/signer-experience/signer-toolbar/signer-toolbar';
import { useAutoSaveSignerExperience } from 'signer-app/signer-experience/use-auto-save-signer-experience';
import { AccessCode } from 'signer-app/signer-experience/access-code';
import { SmsAuthNumberWithFlag } from 'signer-app/signer-experience/sms-auth-number-wrapper';
import { useAtom, useAtomValue } from 'jotai';
import ConfirmationPage from 'signer-app/pages/confirmation-page/confirmation-page';
import { ReassignComplete } from 'signer-app/pages/reassign-complete/reassign-complete';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import { AccessibleDocument as FormView } from 'signer-app/signer-signature-document/accessible-document';
import { FullPageDotLoader } from 'signer-app/parts/loader/dot-loader';
import { FormattedMessage } from 'react-intl';
import {
  postMessage,
  messages,
  useWindowMessage,
  MessageType,
} from 'signer-app/utils/messages';
import { usePreviousValue } from 'signer-app/utils/use-previous-value';
import { useBundleProps } from 'signer-app/bundle-props';
import SignerDeclineComplete from 'signer-app/pages/decline-complete/decline-complete';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';
import { SignerDrawer } from 'signer-app/signer-experience/signer-drawer';
import { Drawer } from '@dropbox/dig-components/drawer';
import { EidComplete } from 'signer-app/pages/eid-complete/eid-complete';
import { useQueryClient } from '@tanstack/react-query';
import { useReady } from 'signer-app/utils/signer-app-ready';
import { Box, ThemeContainer, ThemeProvider } from '@dropbox/dig-foundations';
import { AttachmentsDrawer } from 'signer-app/parts/attachments-drawer/attachments-drawer';
import { SignerAppReadyResponse } from '@dropbox/sign-internal-client';
import { Field } from 'signer-app/types/editor-types';
import { ServerErrorBanner } from './server-error-banner';
import { SignerBannersWrapper } from './signer-banners-wrapper';
import { ReadyErrorBanner } from './ready-error-banner';
import { useBreakpoint } from '@dropbox/dig-components/hooks';
import useIOSViewportFix from './use-ios-viewport-fix';
import { useFeatureFlag } from 'signer-app/context/feature-flags';
import { encodeBase64Url, decodeBase64Url } from 'signer-app/utils/base64-url';

export type SignerAppParams = {
  signatureRequestId: string;
  step:
    | 'accessCode'
    | 'preview'
    | 'sign'
    | 'attachments'
    | 'review'
    | 'delegateComplete'
    | 'declineComplete'
    | 'eidComplete'
    | 'complete';
  fieldId: string | null;
};

export const useSignerAppParams = () => {
  const params = useParams<any>();
  const encodeApiIdsEnabled = useFeatureFlag(
    'sign_core_2026_03_10_encode_api_ids_in_signer_app',
  );

  let fieldId: string | null = null;
  if (params.fieldId) {
    fieldId = encodeApiIdsEnabled
      ? decodeBase64Url(params.fieldId)
      : params.fieldId;
  }

  return {
    signatureRequestId: params.signatureRequestId ?? '',
    step: params.step ?? 'preview',
    fieldId,
  } as SignerAppParams; // I'm casting this because technically step could be any string
};

export const useSignerContext = (): SignerContextShape =>
  React.useContext(signerContext);

export const SignatureWorkflow: React.FC<object> = () => {
  const { embeddedData } = useBundleProps();
  const { step } = useSignerAppParams();
  const isFormViewEnabled = useAtomValue(accessibleViewAtom);
  const { isMobileReady, embeddedParentUrl, redirectUrl } = useSignerContext();

  const stepCanShowDrawer = step === 'sign' || step === 'attachments';
  const stepCanShowAccessibleView =
    step === 'preview' || step === 'sign' || step === 'attachments';
  const showDrawer =
    useShouldShowDrawer() && !isFormViewEnabled && stepCanShowDrawer;
  const { small: displaySmall } = useBreakpoint();

  useIOSViewportFix('document-container');

  let toolbar;
  switch (step) {
    case 'accessCode':
      toolbar = <></>;
      break;
    case 'attachments':
    case 'review':
    case 'preview':
    case 'sign':
      toolbar = <SignerToolbar showNextButton={!showDrawer} />;
      break;

    case 'delegateComplete':
      return <ReassignComplete />;
    case 'declineComplete':
      return (
        <SignerDeclineComplete
          isEmbedded={Boolean(embeddedParentUrl)}
          parentUrl={embeddedParentUrl}
        />
      );
    case 'eidComplete':
      return <EidComplete />;
    case 'complete':
      return (
        <ConfirmationPage
          isEmbedded={Boolean(embeddedParentUrl)}
          parentUrl={embeddedParentUrl}
          signatureId={embeddedData?.signatureId}
          redirectUrl={redirectUrl}
        />
      );
    default:
    /* invalid step - should never happen */
  }

  const content =
    isMobileReady && isFormViewEnabled && stepCanShowAccessibleView ? (
      <Box height="100%" overflowY="auto">
        <FormView />
      </Box>
    ) : (
      <SignerDocument />
    );

  return (
    <div
      className={classnames(styles.container, {
        [styles.mobileContainer]: isFormViewEnabled && !displaySmall,
      })}
      id="document-container"
    >
      {toolbar}

      <Drawer.InlineFrame
        withDrawer={<SignerDrawer isOpen={showDrawer} step={step} />}
        className={styles.documentContainer}
      >
        <SignerBannersWrapper>
          <ServerErrorBanner />
          <ValidationComponent />
        </SignerBannersWrapper>
        {content}
      </Drawer.InlineFrame>
      <AttachmentsDrawer />
    </div>
  );
};

export const SignerExperience: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const { tsmGuid, embeddedData } = useBundleProps();
  const { signatureRequestId, fieldId, step } = useSignerAppParams();
  invariant(signatureRequestId, 'signatureRequestId is required');

  const match = useRouteMatch<{ baseUrl: string }>('/:baseUrl');
  const baseUrl = match?.params?.baseUrl ?? 'sign';
  const history = useHistory();
  const [isAccessibleViewEnabled, setAccessibleView] =
    useAtom(accessibleViewAtom);
  const encodeApiIdsEnabled = useFeatureFlag(
    'sign_core_2026_03_10_encode_api_ids_in_signer_app',
  );
  const setCurrentFieldId = (id: Field['id'] | null) => {
    // Turn off form view if user clicked on document (not field) in review step
    if (!id && step === 'review' && isAccessibleViewEnabled) {
      setAccessibleView(false);
    }

    // Changing a field causes it to be selected, and that should only happen on the signing step.
    const encodedId =
      id && encodeApiIdsEnabled ? encodeBase64Url(id) : id || '';
    history.push(`/${baseUrl}/${signatureRequestId}/sign/${encodedId}`);
  };
  useStartAtCorrectStep(step, baseUrl, signatureRequestId);
  const selectedFieldIds = React.useMemo(() => {
    return fieldId ? [fieldId] : [];
  }, [fieldId]);

  const isDropbox = true;
  React.useEffect(() => {
    if (embeddedData?.parentUrl) {
      postMessage({ type: messages.APP_INITIALIZE }, embeddedData.parentUrl);
    }
  }, [embeddedData?.parentUrl]);

  useWindowMessage(
    MessageType.USER_CANCEL_REQUEST,
    () => {
      if (!embeddedData?.parentUrl) {
        return;
      }
      if (step === 'complete') {
        // The close button was pressed when the user
        // has finished the signature request. Send the
        // "finish" insread of "cancel" message.
        postMessage(
          {
            type: messages.USER_FINISH_REQUEST,
          },
          embeddedData?.parentUrl,
        );
      } else {
        postMessage(
          { type: MessageType.USER_CANCEL_REQUEST },
          embeddedData?.parentUrl,
        );
      }
    },
    { expectedOrigin: embeddedData?.parentUrl },
  );
  const {
    onFieldUpdate,
    data,
    signatureRequest,
    isFetching,
    accessCode,
    setAccessCode,
    uploadAttachment,
    esignDisclosureOptIn,
    finalize,
    flushDraft,
    isFinalizing,
    smsMeta,
    clearAuth,
    setAuthState,
    setSmsMeta,
    numAttemptsLeft,
    redirectUrl,
    backendError,
  } = useAutoSaveSignerExperience(signatureRequestId);

  const ready = useReady();
  const queryClient = useQueryClient();
  const validationErrors = useValidation(signatureRequest);
  const touchField = useTouchField();
  const previousField = usePreviousValue(fieldId);
  React.useEffect(() => {
    if (previousField && previousField !== fieldId) {
      touchField(previousField);
    }
  }, [fieldId, previousField, touchField]);

  // this is FAR from ideal but I don't see a more reliable of ensuring the prefill modal
  // won't be displayed multiple times (e.g. on `complete` step), because the prefill modal is rendered
  // by SignerContext, we can't change it because of the old signer app, and when changing the step
  // from `preview` to `complete`, SignerContext unmounts and remounts. So we need to keep this state here
  // and push it down to the modal
  const [hasSeenPrefillModal, setHasSeenPrefillModal] = React.useState(false);
  const handleOnPrefillModalClose = useCallback(() => {
    setHasSeenPrefillModal(true);
  }, []);
  const handlePrefill = useCallback(() => {
    const firstEditableField = signatureRequest?.fields?.find(
      (f) => !f.readOnly && !f.hidden,
    );
    const encodedId =
      firstEditableField?.id && encodeApiIdsEnabled
        ? encodeBase64Url(firstEditableField.id)
        : firstEditableField?.id ?? '';
    history.push(`/${baseUrl}/${signatureRequestId}/sign/${encodedId}`);
  }, [
    signatureRequest?.fields,
    history,
    baseUrl,
    signatureRequestId,
    encodeApiIdsEnabled,
  ]);

  const [allowCancel, setAllowCancel] = React.useState(true);

  useWindowMessage(
    MessageType.APP_CONFIGURE,
    (data) => {
      setAllowCancel(data.payload.allowCancel !== false);
    },
    { expectedOrigin: embeddedData?.parentUrl },
  );

  if (data === 'need-sms-auth' || data === 'sms-rate-limit') {
    return (
      <SmsAuthNumberWithFlag
        signerNumber={smsMeta?.signerNumber ?? ''}
        numAttemptsLeft={smsMeta?.numAttemptsLeft ?? null}
        requesterName={smsMeta?.requesterName ?? null}
        requesterEmail={smsMeta?.requesterEmail ?? ''}
        onSendSms={() =>
          ready({ tsmGuid, triggerSms: 1 }).then(
            (resp: SignerAppReadyResponse) => {
              const code = resp.error?.code;
              if (code === 'sms_rate_limit') {
                setAuthState('sms-rate-limit');
              }
              setSmsMeta(resp.error?.data ?? null);
              queryClient.setQueryData(
                ['signer-experience', 'ready', tsmGuid],
                resp,
              );
              return resp;
            },
          )
        }
        onVerify={async (smsCode) => {
          try {
            const verificationResp = await ready({
              tsmGuid,
              triggerSms: 0,
              smsAuthCode: smsCode,
            });
            return verificationResp;
          } catch (e) {
            throw e;
          }
        }}
        onSuccess={(resp) => {
          queryClient.setQueriesData(
            { queryKey: ['signer-experience', 'ready', tsmGuid] },
            resp,
          );
          clearAuth();
        }}
        isRateLimited={data === 'sms-rate-limit'}
        isEmbedded={embeddedData?.isEmbeddedSigning}
      />
    );
  }
  if (typeof data === 'string') {
    const handleAccessCodeSubmit = async (code: string) => {
      setAccessCode(code);
    };

    return (
      <AccessCode
        error={data}
        isFetching={isFetching}
        onSubmit={handleAccessCodeSubmit}
        isEmbedded={embeddedData?.isEmbeddedSigning}
        numAttemptsLeft={numAttemptsLeft}
      />
    );
  }

  if (isFetching || !data || !signatureRequest) {
    return (
      <>
        <ReadyErrorBanner />
        <FullPageDotLoader />
      </>
    );
  }

  if (isFinalizing) {
    return (
      <FullPageDotLoader>
        <Text size="large">
          {data.isQualifiedSignature ? (
            <FormattedMessage
              id="66adb1f97622e9aa87a1ae5cd24a4d085b91c81d613827c06f82580f365fd783"
              description="text in signer flow informing signer that the document is being finalized and qualified signature verification is to follow"
              defaultMessage="Please wait a moment while we begin the verification process."
            />
          ) : (
            <FormattedMessage
              id="68621a41cdb52df49768baa2c39df064d55aef5384ffe8f8aeea47a9a73337e6"
              description="text in signer flow informing signer that the document is being finalized as input fields are filled and submitted"
              defaultMessage="We're finalizing your document"
            />
          )}
        </Text>
      </FullPageDotLoader>
    );
  }

  const {
    user,
    isEmbedded,
    canInsertEverywhere,
    allowColorSignature,
    defaultDateFormat,
    allowReassign,
    allowDecline,
    embeddedParentUrl,
    isMobileReady,
    defaultToFormView,
    hideFormViewToggle,
    esignDisclosure,
    isComingFromSmsDelivery,
    requestTitle,
    allowedSignatureTypes,
    defaultSignatureType,
    embeddedRedirectUrl,
    isQualifiedSignature,
  } = data;
  const startEnabled = !esignDisclosure?.enabled || esignDisclosure?.userOptIn;

  return (
    <ThemeProvider theme="vis2023">
      <ThemeContainer>
        <SignatureProviderImpl
          hideSpinner={true}
          isDropbox={isDropbox}
          firstName={user.settings.firstName}
          lastName={user.settings.lastName}
          primarySignatureGuid={user.primarySignatureGuid}
          isEmbedded={isEmbedded}
          canInsertEverywhere={canInsertEverywhere}
          allowColorSig={allowColorSignature}
          allowedSignatureTypes={allowedSignatureTypes}
          defaultSignatureType={defaultSignatureType}
        >
          <TouchProvider>
            <SignerContext
              startEnabled={startEnabled}
              selectedFieldIds={selectedFieldIds}
              onFieldUpdate={onFieldUpdate}
              signatureRequest={signatureRequest}
              uploadAttachment={uploadAttachment}
              setCurrentField={setCurrentFieldId}
              defaultDateFormat={defaultDateFormat}
              allowReassign={!!allowReassign}
              allowDecline={!!allowDecline}
              allowCancel={!!allowCancel}
              accessCode={accessCode}
              embeddedParentUrl={embeddedParentUrl}
              redirectUrl={embeddedRedirectUrl ?? redirectUrl}
              isMobileReady={isMobileReady}
              defaultToFormView={defaultToFormView}
              hideFormViewToggle={hideFormViewToggle}
              esignDisclosure={esignDisclosure}
              isComingFromSmsDelivery={!!isComingFromSmsDelivery}
              esignDisclosureOptIn={esignDisclosureOptIn}
              requestTitle={requestTitle}
              finalize={finalize}
              flushDraft={flushDraft}
              transmissionGuid={tsmGuid}
              isQualifiedSignature={!!isQualifiedSignature}
              validationErrors={validationErrors}
              backendError={backendError}
              onPrefill={handlePrefill}
              hasSeenPrefillModal={hasSeenPrefillModal}
              onPrefillModalClose={handleOnPrefillModalClose}
            >
              {children}
            </SignerContext>
          </TouchProvider>
        </SignatureProviderImpl>
      </ThemeContainer>
    </ThemeProvider>
  );
};
function useStartAtCorrectStep(
  step: string,
  baseUrl: string,
  signatureRequestId: string,
) {
  const history = useHistory();
  const encodeApiIdsEnabled = useFeatureFlag(
    'sign_core_2026_03_10_encode_api_ids_in_signer_app',
  );

  const [hasVisitedValidStartingStep, setHasVisitedValidStartingStep] =
    React.useState(false);

  React.useEffect(() => {
    const isOnStartingStep = encodeApiIdsEnabled
      ? step === 'preview' || step === 'sign'
      : step === 'preview';

    if (!hasVisitedValidStartingStep && !isOnStartingStep) {
      history.push(`/${baseUrl}/${signatureRequestId}/preview/`);
    } else if (isOnStartingStep) {
      setHasVisitedValidStartingStep(true);
    }
  }, [
    baseUrl,
    hasVisitedValidStartingStep,
    history,
    signatureRequestId,
    step,
    encodeApiIdsEnabled,
  ]);
}
